import type { ClientPackageAppendixDocumentDto, ClientPackageChapterDto, ClientPackageDto, SaEngineRequestData } from '../api/generated/client';
import type { IClientPackageChapter } from '../interfaces/package';

/**
 * Get the client package with status DRAFT from all given client packages. (there is always only 1)
 * @param {IPackage[]} packages
 * @return {IPackage | undefined}
 */
export const getDraftClientPackage = (packages: ClientPackageDto[]): ClientPackageDto | undefined => packages?.find((p) => p.status === 'DRAFT');

/**
 * Sort finalised packages from newest to oldest finalisedAt date
 * @param {IPackage[]} packages
 * @return {IPackage[]}
 */
export const getSortedFinalizedClientPackages = (packages: ClientPackageDto[]): ClientPackageDto[] =>
    packages.filter((cp) => !!cp.finalizedAt).sort((packageA, packageB) => packageB.id - packageA.id);

/**
 * Get the client-package chapter by the given name and return the list of chapters without the separated chapter.
 * @param {IPackage} clientPackage
 * @param {string} chapterName
 * @return {{chapter: IClientPackageChapter, chapters: IClientPackageChapter[]}}
 */
export const getSplitClientPackageChapter = (clientPackage: ClientPackageDto, chapterName: string) => {
    const chapters = [...clientPackage.clientPackageChapters];
    const chapterIndex = chapters.findIndex((chapter) => chapterName.includes(chapter.chapterName));
    const chapter = chapterIndex >= 0 ? chapters.splice(chapterIndex, 1)[0] : undefined;

    return { chapter, chapters };
};

/**
 * Updates the selected status of the given chapter for the given clientPackage.
 * @param {IPackage} clientPackage
 * @param {string} chapterName
 * @param {boolean} selected
 * @return {IClientPackageChapter[]}
 */
export const getUpdatedClientPackageChapter = (
    clientPackage: ClientPackageDto,
    chapterName: string,
    selected: boolean,
): Pick<ClientPackageDto, 'clientPackageChapters'> => {
    const { chapter, chapters } = getSplitClientPackageChapter(clientPackage, chapterName);
    const updatedChapter: IClientPackageChapter =
        chapter != null ? { ...chapter, selected } : { chapterName, selected, position: Math.max(...chapters.map((c) => c.position)) + 1 };

    return {
        clientPackageChapters: [
            ...chapters,
            {
                ...updatedChapter,
                clientId: chapter?.clientId,
            } as ClientPackageChapterDto,
        ],
    };
};

/**
 * Updates the `withDetails` flag of the given chapter (used by the Liquidity Calculation chapter
 * to toggle the detail pages) and returns the updated list of chapters.
 * @param {ClientPackageDto} clientPackage
 * @param {string} chapterName
 * @param {boolean} withDetails
 * @return {Pick<ClientPackageDto, 'clientPackageChapters'>}
 */
export const getUpdatedClientPackageChapterWithDetails = (
    clientPackage: ClientPackageDto,
    chapterName: string,
    withDetails: boolean,
): Pick<ClientPackageDto, 'clientPackageChapters'> => {
    const { chapter, chapters } = getSplitClientPackageChapter(clientPackage, chapterName);

    if (chapter == null) {
        return { clientPackageChapters: clientPackage.clientPackageChapters };
    }

    return { clientPackageChapters: [...chapters, { ...chapter, withDetails }] };
};

/**
 * Get the client-package appendix by the given documentId and return the list of appendixes without the separated appendix.
 * @param {IPackage} clientPackage
 * @param {number} documentId
 * @return {{appendix: IClientPackageAppendixDocument, appendixDocuments: IClientPackageAppendixDocument[]}}
 */
export const getSplitClientPackageAppendix = (clientPackage: ClientPackageDto, documentId: number) => {
    const appendixDocuments = [...clientPackage.clientPackageAppendixDocuments];
    const appendixIndex = appendixDocuments.findIndex((a) => a.documentId === documentId);
    const appendix = appendixIndex >= 0 ? appendixDocuments.splice(appendixIndex, 1)[0] : undefined;

    return { appendix, appendixDocuments };
};

/**
 * Updates the selected status of the given appendix in the given clientPackage's appendix documents.
 * @param {IPackage} clientPackage
 * @param {IClientPackageAppendixDocument} appendixToUpdate
 * @param {boolean} isSelected
 * @return {IClientPackageAppendixDocument[]}
 */
export const getUpdatedAppendixDocuments = (
    clientPackage: ClientPackageDto,
    appendixToUpdate: ClientPackageAppendixDocumentDto,
    isSelected?: boolean,
): Pick<ClientPackageDto, 'clientPackageAppendixDocuments'> => {
    const { appendix, appendixDocuments } = getSplitClientPackageAppendix(clientPackage, appendixToUpdate.documentId);
    const selected = isSelected != null ? isSelected : (appendix ?? appendixToUpdate).selected;
    const updatedAppendix: ClientPackageAppendixDocumentDto = appendix != null ? { ...appendix, selected } : { ...appendixToUpdate, selected };

    return { clientPackageAppendixDocuments: [...appendixDocuments, updatedAppendix] };
};

/**
 * Get the client-package document by the given documentId and return the list of documents without the separated document.
 * @param {IPackage} clientPackage
 * @param {number} documentId
 * @return {{documents: IPackageDocument[], document: IPackageDocument}}
 */
export const getSplitClientPackageDocument = (clientPackage: ClientPackageDto, documentId: number) => {
    const documents = [...clientPackage.clientPackageDocuments];
    const documentIndex = documents.findIndex((d) => d.documentId === documentId);
    const document = documentIndex >= 0 ? documents.splice(documentIndex, 1)[0] : undefined;

    return { document, documents };
};

/**
 * Updates the selected status of the given appendix in the given clientPackage's appendix documents.
 * @param {IPackage} clientPackage
 * @param {number} documentId
 * @param {boolean} selected
 * @return {IPackageDocument[]}
 */
export const getUpdatedClientPackageDocument = (
    clientPackage: ClientPackageDto,
    documentId: number,
    selected: boolean,
): Pick<ClientPackageDto, 'clientPackageDocuments'> => {
    const { document, documents } = getSplitClientPackageDocument(clientPackage, documentId);

    return { clientPackageDocuments: [...documents, ...(document ? [{ ...document, selected }] : [])] };
};

export const addProjectionRequestDataToClientPackage = (clientPackage: ClientPackageDto, projectionSettings: SaEngineRequestData): ClientPackageDto => {
    const chapterIndex = clientPackage.clientPackageChapters.findIndex((ch) => ch.chapterName === 'PROJECTION');

    const newChapters = [...clientPackage.clientPackageChapters];

    if (chapterIndex !== -1) {
        newChapters[chapterIndex] = {
            ...newChapters[chapterIndex],
            saeEngineRequestData: projectionSettings,
        };
    }

    return {
        ...clientPackage,
        clientPackageChapters: newChapters,
    };
};
